import { inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

import { UserService } from '../services/user.service';
import { AppStateResetService } from '../services/app-state-reset.service';

const LOG_LIMIT = 50;

/**
 * Interim, session-scoped client capture of query text the app couldn't do anything with
 * (unmatched, or matched an unsupported-question shape) — a stopgap until a backend logging
 * endpoint exists (design-spec scope decision #1). Query text ONLY, no user identifier stored
 * alongside an entry; the sessionStorage key itself is per-identity for isolation between
 * concurrent users on the same browser, same as `SearchHistoryStore`.
 */
@Injectable({ providedIn: 'root' })
export class SearchUnmatchedQueryLogStore {
  private readonly user = inject(UserService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly appReset = inject(AppStateResetService);

  readonly entries = signal<string[]>([]);

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      // The read itself is inside the try (CodeAnt review, PR #263): `getItem` can throw,
      // not just `JSON.parse` — e.g. Safari private-mode / storage-disabled browsers reject
      // ANY sessionStorage access. This store is root-provided and read by the always-on
      // header, so an uncaught throw here would take down the whole app shell instead of
      // degrading to an empty in-memory log.
      try {
        const stored = sessionStorage.getItem(this.key());
        if (stored) {
          const parsed: unknown = JSON.parse(stored);
          // A validly-parsed but wrong-shaped value (an object, an array of non-strings, …)
          // is just as unsafe to trust as malformed JSON — the always-on header's `@for` loop
          // over `entries()` would throw on a non-string entry, taking down the whole shell.
          if (Array.isArray(parsed) && parsed.every((v) => typeof v === 'string')) {
            this.entries.set(parsed);
          }
        }
      } catch { /* storage unavailable or corrupt value, start empty */ }
    }
    this.appReset.register(() => {
      this.entries.set([]);
      if (isPlatformBrowser(this.platformId)) sessionStorage.removeItem(this.key());
    });
  }

  private key(): string {
    return `search-unmatched-log:${this.user.getCustomerId()}|${this.user.getUserId()}`;
  }

  /** No de-dupe — unlike `SearchHistoryStore`, every occurrence is real signal about what people
      are actually typing, not a "recent items" list a duplicate would clutter. */
  record(query: string): void {
    const next = [...this.entries(), query].slice(-LOG_LIMIT);
    this.entries.set(next);
    if (isPlatformBrowser(this.platformId)) {
      // Persistence is best-effort: a quota-exceeded or privacy-mode `setItem` throw must not
      // propagate. `record()` runs as the FIRST statement inside `IntentSearchComponent.onBlur()`
      // — an uncaught throw here would skip `focused.set(false)` and wedge the suggestions
      // panel open on every subsequent blur. Degrade to in-memory-only rather than break the
      // feature.
      try { sessionStorage.setItem(this.key(), JSON.stringify(next)); } catch { /* storage unavailable, in-memory state above still applies */ }
    }
  }
}
